import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ScrollService {

  private getRootScrollContainer(): HTMLElement {
    return (document.scrollingElement as HTMLElement | null) ?? document.documentElement ?? document.body;
  }

  private resolveScrollContainer(element?: HTMLElement): HTMLElement {
    if (element) {
      return this.getScrollContainer(element) ?? this.getRootScrollContainer();
    }

    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      return this.getScrollContainer(activeElement) ?? this.getRootScrollContainer();
    }

    return this.getRootScrollContainer();
  }

  private getScrollContainer(element: HTMLElement): HTMLElement | null {
    let parent = element.parentElement;

    while (parent) {
      const styles = getComputedStyle(parent);
      const overflowY = styles.overflowY;
      const canScroll = /(auto|scroll|overlay)/.test(overflowY) && parent.scrollHeight > parent.clientHeight;

      if (canScroll) {
        return parent;
      }

      parent = parent.parentElement;
    }

    return null;
  }

  /**
   * Scrolls smoothly to the top of the page
   * @param duration - Duration of the scroll animation in milliseconds (default: 500)
   * @param element - Target element to scroll (default: window)
   */
  scrollToTop(duration: number = 500, element?: HTMLElement): void {
    const scrollContainer = this.resolveScrollContainer(element);
    const startPosition = scrollContainer.scrollTop;
    
    // If already at top, don't animate
    if (startPosition === 0) return;
    
    const startTime = performance.now();

    const animateScroll = (currentTime: number) => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / duration, 1);
      
      // Use a gentler easing function (easeOutQuad)
      const ease = progress * (2 - progress);
      
      // Calculate position: interpolate from start to 0
      const currentPosition = Math.round(startPosition * (1 - ease));

      scrollContainer.scrollTop = currentPosition;
      if (!element) {
        window.scrollTo(0, currentPosition);
      }

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  }

  /**
   * Instantly scrolls to the top without animation
   * @param element - Target element to scroll (default: window)
   */
  scrollToTopInstant(element?: HTMLElement): void {
    this.resolveScrollContainer(element).scrollTop = 0;
  }

  /**
   * Scrolls to a specific element smoothly
   * @param targetElement - The element to scroll to
   * @param duration - Duration of the scroll animation in milliseconds (default: 500)
   * @param offset - Offset from the top of the element in pixels (default: 0)
   */
  scrollToElement(targetElement: HTMLElement, duration: number = 500, offset: number = 0): void {
    const scrollContainer = this.getScrollContainer(targetElement);
    if (!scrollContainer) {
      const startPosition = window.pageYOffset;
      const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset - offset;
      const distance = targetPosition - startPosition;
      const startTime = performance.now();

      const animateScroll = (currentTime: number) => {
        const elapsedTime = currentTime - startTime;
        const progress = Math.min(elapsedTime / duration, 1);

        // Easing function for smooth animation (easeOutCubic)
        const ease = 1 - Math.pow(1 - progress, 3);
        const currentPosition = startPosition + (distance * ease);

        window.scrollTo(0, currentPosition);

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };

      requestAnimationFrame(animateScroll);
      return;
    }

    const startPosition = scrollContainer.scrollTop;
    const targetPosition =
      targetElement.getBoundingClientRect().top - scrollContainer.getBoundingClientRect().top + startPosition - offset;
    const distance = targetPosition - startPosition;
    const startTime = performance.now();

    const animateScroll = (currentTime: number) => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / duration, 1);

      // Easing function for smooth animation (easeOutCubic)
      const ease = 1 - Math.pow(1 - progress, 3);
      const currentPosition = startPosition + distance * ease;

      scrollContainer.scrollTop = currentPosition;

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  }
}
